import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {Shield, CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw, Ban, LogOut} from 'lucide-react'
import DashboardSwitcher from "../components/DashboardSwitcher"

interface RecruiterMOU {
  _id: string
  userId: string
  mouStatus: "unsigned" | "signed" | "suspended" | "expired"
  mouSignedAt: string | null
  mouExpiresAt: string | null
  mouApprovedBy: string | null
  suspensionReason: string | null
  companyName: string
  companyWebsite: string
  contactEmail: string
  contactPhone: string
  createdAt: string
  updatedAt: string
}

export default function AdminMOUApproval() {
  const navigate = useNavigate()
  const [mouRecords, setMouRecords] = useState<RecruiterMOU[]>([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState<string | null>(null)
  const [suspensionModal, setSuspensionModal] = useState<{ show: boolean; recruiterId: string | null; reason: string }>({
    show: false,
    recruiterId: null,
    reason: ""
  })

  useEffect(() => {
    checkAdminAccess()
    loadMOURecords()
  }, [])

  const checkAdminAccess = () => {
    const user = lumi.auth.user
    if (!user || user.userRole !== "ADMIN") {
      toast.error("Admin access required")
      navigate("/auth")
    }
  }

  const loadMOURecords = async () => {
    try {
      const { list } = await lumi.entities.RecruiterMOU.list({
        sort: { createdAt: -1 },
        limit: 100
      })
      setMouRecords(list as unknown as RecruiterMOU[])
    } catch (error) {
      console.error("Failed to load MOU records:", error)
      toast.error("Failed to load MOU records")
    } finally {
      setLoading(false)
    }
  }

  const handleApproveMOU = async (recruiterId: string) => {
    setProcessing(recruiterId)
    try {
      const record = mouRecords.find(r => r.userId === recruiterId)
      if (!record) {
        toast.error("Record not found")
        return
      }

      const now = new Date()
      const expiresAt = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000) // 1 year

      await lumi.entities.RecruiterMOU.update(record._id, {
        mouStatus: "signed",
        mouSignedAt: now.toISOString(),
        mouExpiresAt: expiresAt.toISOString(),
        mouApprovedBy: lumi.auth.user?.userId || "admin",
        updatedAt: now.toISOString()
      })

      // Log audit
      await lumi.entities.AuditLogs.create({
        userId: lumi.auth.user?.userId || "admin",
        targetUserId: recruiterId,
        action: "MOU_APPROVE",
        description: `Admin approved MOU for ${record.companyName}`,
        metadata: JSON.stringify({ expiresAt: expiresAt.toISOString() }),
        timestamp: now.toISOString()
      })

      toast.success("MOU approved successfully")
      loadMOURecords()
    } catch (error) {
      console.error("Failed to approve MOU:", error)
      toast.error("Failed to approve MOU")
    } finally {
      setProcessing(null)
    }
  }

  const handleSuspendMOU = async () => {
    if (!suspensionModal.recruiterId || !suspensionModal.reason.trim()) {
      toast.error("Suspension reason is required")
      return
    }

    setProcessing(suspensionModal.recruiterId)
    try {
      const record = mouRecords.find(r => r.userId === suspensionModal.recruiterId)
      if (!record) {
        toast.error("Record not found")
        return
      }

      await lumi.entities.RecruiterMOU.update(record._id, {
        mouStatus: "suspended",
        suspensionReason: suspensionModal.reason,
        updatedAt: new Date().toISOString()
      })

      // Log audit
      await lumi.entities.AuditLogs.create({
        userId: lumi.auth.user?.userId || "admin",
        targetUserId: suspensionModal.recruiterId,
        action: "MOU_SUSPEND",
        description: `Admin suspended MOU for ${record.companyName}`,
        metadata: JSON.stringify({ reason: suspensionModal.reason }),
        timestamp: new Date().toISOString()
      })

      toast.success("MOU suspended successfully")
      setSuspensionModal({ show: false, recruiterId: null, reason: "" })
      loadMOURecords()
    } catch (error) {
      console.error("Failed to suspend MOU:", error)
      toast.error("Failed to suspend MOU")
    } finally {
      setProcessing(null)
    }
  }

  const handleReinstateMOU = async (recruiterId: string) => {
    setProcessing(recruiterId)
    try {
      const record = mouRecords.find(r => r.userId === recruiterId)
      if (!record) {
        toast.error("Record not found")
        return
      }

      await lumi.entities.RecruiterMOU.update(record._id, {
        mouStatus: "signed",
        suspensionReason: null,
        updatedAt: new Date().toISOString()
      })

      // Log audit
      await lumi.entities.AuditLogs.create({
        userId: lumi.auth.user?.userId || "admin",
        targetUserId: recruiterId,
        action: "MOU_REINSTATE",
        description: `Admin reinstated MOU for ${record.companyName}`,
        metadata: JSON.stringify({}),
        timestamp: new Date().toISOString()
      })

      toast.success("MOU reinstated successfully")
      loadMOURecords()
    } catch (error) {
      console.error("Failed to reinstate MOU:", error)
      toast.error("Failed to reinstate MOU")
    } finally {
      setProcessing(null)
    }
  }

  const handleRenewMOU = async (recruiterId: string) => {
    setProcessing(recruiterId)
    try {
      const record = mouRecords.find(r => r.userId === recruiterId)
      if (!record) {
        toast.error("Record not found")
        return
      }

      const now = new Date()
      const expiresAt = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000)

      await lumi.entities.RecruiterMOU.update(record._id, {
        mouStatus: "signed",
        mouExpiresAt: expiresAt.toISOString(),
        mouApprovedBy: lumi.auth.user?.userId || "admin",
        updatedAt: now.toISOString()
      })

      // Log audit
      await lumi.entities.AuditLogs.create({
        userId: lumi.auth.user?.userId || "admin",
        targetUserId: recruiterId,
        action: "MOU_RENEW",
        description: `Admin renewed MOU for ${record.companyName}`,
        metadata: JSON.stringify({ newExpiresAt: expiresAt.toISOString() }),
        timestamp: now.toISOString()
      })

      toast.success("MOU renewed successfully")
      loadMOURecords()
    } catch (error) {
      console.error("Failed to renew MOU:", error)
      toast.error("Failed to renew MOU")
    } finally {
      setProcessing(null)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "signed":
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="w-3 h-3" />Signed</span>
      case "unsigned":
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"><Clock className="w-3 h-3" />Unsigned</span>
      case "suspended":
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"><XCircle className="w-3 h-3" />Suspended</span>
      case "expired":
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><AlertTriangle className="w-3 h-3" />Expired</span>
      default:
        return <span className="text-gray-500">Unknown</span>
    }
  }

  const getDaysUntilExpiry = (expiresAt: string | null): number | null => {
    if (!expiresAt) return null
    const expiry = new Date(expiresAt)
    const now = new Date()
    const diff = expiry.getTime() - now.getTime()
    return Math.floor(diff / (1000 * 60 * 60 * 24))
  }

  const handleSignOut = () => {
    lumi.auth.signOut()
    toast.success("Signed out successfully")
    navigate("/")
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading MOU records...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-emerald-600" />
            <h1 className="text-xl font-bold text-gray-900">MOU Management</h1>
          </div>
          <div className="flex items-center gap-4">
            <DashboardSwitcher />
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-500">
            <div className="text-2xl font-bold text-gray-900">{mouRecords.filter(r => r.mouStatus === "signed").length}</div>
            <div className="text-sm text-gray-600">Signed MOUs</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-gray-400">
            <div className="text-2xl font-bold text-gray-900">{mouRecords.filter(r => r.mouStatus === "unsigned").length}</div>
            <div className="text-sm text-gray-600">Pending Approval</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-red-500">
            <div className="text-2xl font-bold text-gray-900">{mouRecords.filter(r => r.mouStatus === "suspended").length}</div>
            <div className="text-sm text-gray-600">Suspended</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-yellow-500">
            <div className="text-2xl font-bold text-gray-900">{mouRecords.filter(r => r.mouStatus === "expired").length}</div>
            <div className="text-sm text-gray-600">Expired</div>
          </div>
        </div>

        {/* MOU Records Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Signed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mouRecords.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      No MOU records found
                    </td>
                  </tr>
                ) : (
                  mouRecords.map((record) => {
                    const daysLeft = getDaysUntilExpiry(record.mouExpiresAt)
                    return (
                      <tr key={record._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{record.companyName || "N/A"}</div>
                          <div className="text-xs text-gray-500">{record.companyWebsite || "N/A"}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{record.contactEmail || "N/A"}</div>
                          <div className="text-xs text-gray-500">{record.contactPhone || "N/A"}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(record.mouStatus)}
                          {record.suspensionReason && (
                            <div className="text-xs text-red-600 mt-1">Reason: {record.suspensionReason}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.mouSignedAt ? new Date(record.mouSignedAt).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {record.mouExpiresAt ? (
                            <div>
                              <div className="text-sm text-gray-900">{new Date(record.mouExpiresAt).toLocaleDateString()}</div>
                              {daysLeft !== null && (
                                <div className={`text-xs ${daysLeft <= 30 ? "text-red-600" : "text-gray-500"}`}>
                                  {daysLeft > 0 ? `${daysLeft} days left` : "Expired"}
                                </div>
                              )}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            {record.mouStatus === "unsigned" && (
                              <button
                                onClick={() => handleApproveMOU(record.userId)}
                                disabled={processing === record.userId}
                                className="text-green-600 hover:text-green-800 font-medium disabled:opacity-50"
                              >
                                {processing === record.userId ? "..." : "Approve"}
                              </button>
                            )}
                            {record.mouStatus === "signed" && (
                              <>
                                <button
                                  onClick={() => setSuspensionModal({ show: true, recruiterId: record.userId, reason: "" })}
                                  disabled={processing === record.userId}
                                  className="text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                                >
                                  <Ban className="w-4 h-4 inline" />
                                </button>
                                {daysLeft !== null && daysLeft <= 60 && (
                                  <button
                                    onClick={() => handleRenewMOU(record.userId)}
                                    disabled={processing === record.userId}
                                    className="text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                                  >
                                    <RefreshCw className="w-4 h-4 inline" />
                                  </button>
                                )}
                              </>
                            )}
                            {record.mouStatus === "suspended" && (
                              <button
                                onClick={() => handleReinstateMOU(record.userId)}
                                disabled={processing === record.userId}
                                className="text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                              >
                                {processing === record.userId ? "..." : "Reinstate"}
                              </button>
                            )}
                            {record.mouStatus === "expired" && (
                              <button
                                onClick={() => handleRenewMOU(record.userId)}
                                disabled={processing === record.userId}
                                className="text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                              >
                                {processing === record.userId ? "..." : "Renew"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Suspension Modal */}
      {suspensionModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Suspend MOU</h3>
            <p className="text-sm text-gray-600 mb-4">Please provide a reason for suspending this recruiter's MOU:</p>
            <textarea
              value={suspensionModal.reason}
              onChange={(e) => setSuspensionModal({ ...suspensionModal, reason: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent mb-4"
              rows={3}
              placeholder="Violation details..."
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setSuspensionModal({ show: false, recruiterId: null, reason: "" })}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSuspendMOU}
                disabled={!suspensionModal.reason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Suspend MOU
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
